import React, { useCallback, useState, useEffect } from 'react';
import {
  useAppMessage,
  useAudioTrack,
  useDaily,
  useLocalSessionId,
  useScreenShare,
  useVideoTrack,
  useMeetingSessionState,
  useDailyEvent
} from '@daily-co/daily-react';
import { confirmAlert } from 'react-confirm-alert';
import { isTutorParticipant } from '../participantRoles';
import 'react-confirm-alert/src/react-confirm-alert.css';

import MeetingInformation from '../MeetingInformation/MeetingInformation';
//import Chat from '../Chat/Chat';

import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import VoiceOverOffIcon from '@mui/icons-material/VoiceOverOff';

import './Tray.css';
import { toast } from '../../hooks/use-toast';
import { useIsPhone } from '../../../hooks/useMediaQuery';
import {
  CameraOn,
  Leave,
  CameraOff,
  MicrophoneOff,
  MicrophoneOn,
  Screenshare,
  Info,
  //ChatIcon,
  //ChatHighlighted,
  //EarOn,
  //EarOff,
} from './Icons';
import { useAuth } from '../../../contexts/AuthContext';

import { Speech, SpeechOff, Ear, EarOff, Presentation, SquareUser, UserX } from 'lucide-react';
import { useHandRaising } from '../HandRaising/useHandRaising';
import { Hand } from 'lucide-react';

export default function Tray({ tutorEmails, leaveCall }) {
  const callObject = useDaily();
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare();
  const { user } = useAuth();
  const isPhone = useIsPhone();
  // Touch has no hover, so the inert student mic's tooltip never shows on a phone.
  const showRaiseHandHint = () => toast({ description: 'Raise your hand to speak to your tutor.' });

  // this state exists on the tutor side and is only being updated on the tutor side
  const [remoteStudentsCanHear, setRemoteStudentsCanHear] = useState(true);
  const [remoteStudentsMicsMuted, setRemoteStudentsMicsMuted] = useState(false);

  const [showMeetingInformation, setShowMeetingInformation] = useState(false);
  //const [showChat, setShowChat] = useState(false);
  //const [newChatMessage, setNewChatMessage] = useState(false);

  const localSessionId = useLocalSessionId();
  const localVideo = useVideoTrack(localSessionId);
  const localAudio = useAudioTrack(localSessionId);
  const mutedVideo = localVideo.isOff;
  const mutedAudio = localAudio.isOff;

  const  { data }  = useMeetingSessionState();

  const [localMicMuted, setLocalMicMuted] = useState(false);

  const { raiseHand, lowerHand, handRaisedParticipants } = useHandRaising();
  const localParticipantHasHandRaised = handRaisedParticipants?.includes(localSessionId);

  // Listen for mic state updates.
  // Dep is user?.email (a string) rather than the whole user object so the
  // callback is not recreated on every auth-context re-render that leaves the
  // email unchanged.
  useDailyEvent(
    'app-message',
    useCallback(({ data }) => {
      if (data.type === 'update-mic-state' && !tutorEmails.includes(user?.email)) {
        setLocalMicMuted(data.data.isMuted);
      }
    }, [tutorEmails, user?.email])
  );

  /*useEffect(() => {
    console.log("this is the meeting data", data);
  }, [data]);*/


 

  /* When a remote participant sends a message in the chat, we want to display a differently colored
   * chat icon in the Tray as a notification. By listening for the `"app-message"` event we'll know
   * when someone has sent a message. */
  
  /*useAppMessage({
    onAppMessage: useCallback(() => {
     
      if (!showChat) {
        setNewChatMessage(true);
      }
    }, [showChat])
  }); 
*/
  const toggleVideo = useCallback(() => {
    callObject.setLocalVideo(mutedVideo);
  }, [callObject, mutedVideo]);

  const toggleAudio = useCallback(() => {
    // Only allow toggle if not muted by tutor
    if (!localMicMuted) {
      callObject.setLocalAudio(mutedAudio);
    }
  }, [callObject, mutedAudio, localMicMuted]);

  const toggleStudentsCanHear = useCallback(() => {
    if (!callObject) return;

    const newState = !remoteStudentsCanHear;
    setRemoteStudentsCanHear(newState);

    // Send message to all participants
    callObject.sendAppMessage({
      type: 'update-hearing-state',
      data: { canHear: newState }
    }, '*');
  }, [callObject, remoteStudentsCanHear]);

  const toggleLessonView = useCallback(() => {
    if (!callObject) return;

    // Get the current value, defaulting to false if undefined

    

    const currentValue = data?.LessonView ?? false;

    callObject.setMeetingSessionData({LessonView: !currentValue}, 'replace' );

    //console.log("this is the meeting data", data);



  }, [callObject, data]);

  /*useDailyEvent('meeting-session-state-updated', useCallback(({data}) => {
    console.log("this is the meeting data", data);
  }, [data, callObject]));*/


  const toggleScreenShare = () => isSharingScreen ? stopScreenShare() : startScreenShare();

  const toggleMeetingInformation = () => {
    setShowMeetingInformation(!showMeetingInformation);
  };

  /*
  const toggleChat = () => {
    setShowChat(!showChat);
    if (newChatMessage) {
      setNewChatMessage(!newChatMessage);
    }
  };
  */

  const toggleAllStudentMics = useCallback(() => {
    if (!callObject) return;

    const newState = !remoteStudentsMicsMuted;
    setRemoteStudentsMicsMuted(newState);

    callObject.sendAppMessage({
      type: 'update-mic-state',
      data: { isMuted: newState }
    }, '*');
  }, [callObject, remoteStudentsMicsMuted]);

  const toggleHandRaising = useCallback(() => {
    localParticipantHasHandRaised ? lowerHand() : raiseHand();
  }, [localParticipantHasHandRaised, lowerHand, raiseHand]);

  const kickAllStudents = useCallback(() => {
    confirmAlert({
      title: 'Kick All Students',
      message: 'Are you sure you want to kick all students out of the call?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              const participants = callObject.participants();
              const studentsToKick = Object.values(participants)
                .filter(p => !isTutorParticipant(p, tutorEmails) && !p.local);
              
              // Kick each student
              for (const student of studentsToKick) {
                await callObject.updateParticipant(student.session_id, { eject: true });
              }
              
              console.log(`Successfully kicked ${studentsToKick.length} students`);
            } catch (error) {
              console.error('Failed to kick students:', error);
              // TODO: Add user-friendly error message
            }
          }
        },
        {
          label: 'Cancel',
          onClick: () => {}
        }
      ],
      closeOnClickOutside: false,
      closeOnEscape: false
    });
  }, [callObject, tutorEmails]);

  const isStudent = !tutorEmails.includes(user?.email);

  return (
    <div className={`tray${isStudent ? ' tray--student' : ''}`}>
      {showMeetingInformation && <MeetingInformation />}
      {/*  The chat messages 'live' in the <Chat/> component's state. We can't just remove the component */}
      {/*  from the DOM when hiding the chat, because that would cause us to lose that state. So we're */}
      {/*  choosing a slightly different approach of toggling the chat: always render the component, but only */}
      {/*  render its HTML when showChat is set to true. */}

      {/*   We're also passing down the toggleChat() function to the component, so we can open and close the chat */}
      {/*   from the chat UI and not just the Tray. */}
      {/* <Chat showChat={showChat} toggleChat={toggleChat} /> */}
      <div className="tray-buttons-container">
        <div className="controls">
          <button
            onClick={toggleVideo}
            type="button"
            aria-label={mutedVideo ? 'Turn camera on' : 'Turn camera off'}
            data-tooltip={mutedVideo ? 'Turn camera on' : 'Turn camera off'}
          >
            {mutedVideo ? <CameraOff /> : <CameraOn />}
            {mutedVideo ? 'Turn camera on' : 'Turn camera off'}
          </button>
          <button
            onClick={tutorEmails.includes(user?.email) ? toggleAudio : isPhone ? showRaiseHandHint : undefined}
            type="button"
            aria-label={tutorEmails.includes(user?.email)
              ? (mutedAudio || localMicMuted ? 'Unmute mic' : 'Mute mic')
              : 'Raise your hand to speak to your tutor'}
            data-tooltip={tutorEmails.includes(user?.email)
              ? (mutedAudio || localMicMuted ? 'Unmute mic' : 'Mute mic')
              : 'Raise your hand to speak to your tutor'}
            /* Not `disabled`: disabled buttons don't fire hover, which would hide
               this (important) tooltip for students. Inert via no-op onClick +
               aria-disabled + a greyed style instead, so it stays hoverable. */
            aria-disabled={!tutorEmails.includes(user?.email)}
            className={!tutorEmails.includes(user?.email) ? 'tray-btn--inert' : undefined}
          >
            {mutedAudio || localMicMuted ? <MicrophoneOff /> : <MicrophoneOn />}
            {tutorEmails.includes(user?.email) 
              ? (mutedAudio || localMicMuted ? 'Unmute mic' : 'Mute mic')
              : (mutedAudio || localMicMuted ? <span style={{ color: '#f63135' }}>Raise your hand to speak to your tutor</span> : '')}
          </button>
          {tutorEmails.includes(user?.email) && (
            <>
            <button onClick={toggleStudentsCanHear} type="button">
              {remoteStudentsCanHear ? <Ear /> : <EarOff color="#f63135"/>}
              {remoteStudentsCanHear ? 'Mute students\' hearing' : 'Unmute students\' hearing'}
            </button>
            <button onClick={toggleLessonView} type="button">
                {data?.LessonView ? <SquareUser/>: <Presentation/>}
                {data?.LessonView ? 'Show Student' : 'Show Lesson'}
            </button>
            <button onClick={toggleAllStudentMics} type="button">
              {remoteStudentsMicsMuted ? 
                <VoiceOverOffIcon sx={{ color: '#f63135' }} /> : 
                <RecordVoiceOverIcon />
              }
              {remoteStudentsMicsMuted ? 'Unmute all students' : 'Mute all students'}
            </button>
            <button onClick={kickAllStudents} type="button">
              <UserX color="#f63135" />
              Kick all students
            </button>
            </>
          )}
          {!tutorEmails.includes(user?.email) && (
            <button
              onClick={toggleHandRaising}
              type="button"
              aria-label={localParticipantHasHandRaised ? 'Lower hand' : 'Raise hand'}
              data-tooltip={localParticipantHasHandRaised ? 'Lower hand' : 'Raise hand'}
            >
              <Hand color={localParticipantHasHandRaised ? "#25D366" : undefined} />
              {localParticipantHasHandRaised ? 'Lower hand' : 'Raise hand'}
            </button>
          )}
        </div>
        <div className="actions">
          {/*<button onClick={toggleScreenShare} type="button">*/}
            {/*<Screenshare />*/}
            {/*isSharingScreen ? 'Stop sharing screen' : 'Share screen'*/}
          {/*</button>*/}
          {/*<button onClick={toggleMeetingInformation} type="button">*/}
            {/*<Info />*/}
            {/*{showMeetingInformation ? 'Hide info' : 'Show info'}*/}
          {/*</button>*/}
         
          {/*<button onClick={toggleChat} type="button"> */}
            {/* newChatMessage ? <ChatHighlighted /> : <ChatIcon /> */}
            {/* {showChat ? 'Hide chat' : 'Show chat'} */}
          {/*</button> */}
        </div> 
        <div className="leave">
          <button onClick={leaveCall} type="button" aria-label="Leave call" data-tooltip="Leave call">
            <Leave /> Leave call
          </button>
        </div>
      </div>
    </div>
  );
}